import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, CircularProgress } from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ChatIcon from "@mui/icons-material/Chat";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, CartesianGrid } from "recharts";
import { getOverview } from "../api/adminApi";
import ChartCard from "../components/ChartCard";
import DateRangeToggle from "../components/DateRangeToggle";
import StatCard from "../components/StatCard";

const PIE_COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#3b82f6"];
const STATUS_LABELS = { active: "Active", closed: "Closed", live_requested: "Live Requested" };

const Analytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("30");

  useEffect(() => {
    setLoading(true);
    getOverview(days).then(setData).catch(() => { }).finally(() => setLoading(false));
  }, [days]);

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;

  const stats = [
    { icon: <PeopleIcon sx={{ color: "#6366f1", fontSize: 22 }} />, label: "Total Users", value: data?.totalUsers?.toLocaleString() || 0, color: "#6366f1" },
    { icon: <SmartToyIcon sx={{ color: "#10b981", fontSize: 22 }} />, label: "Total Bots", value: data?.totalBots?.toLocaleString() || 0, color: "#10b981" },
    { icon: <ChatIcon sx={{ color: "#f59e0b", fontSize: 22 }} />, label: "Conversations", value: data?.totalConversations?.toLocaleString() || 0, color: "#f59e0b" },
    { icon: <WorkspacePremiumIcon sx={{ color: "#8b5cf6", fontSize: 22 }} />, label: "Active Plans", value: data?.totalPlans || 0, color: "#8b5cf6" },
  ];

  const dailyData = data?.dailyConversations?.labels?.map((label, i) => ({
    date: label.slice(5),
    conversations: data.dailyConversations.values[i],
  })) || [];

  const statusData = Object.entries(data?.statusBreakdown || {}).map(([key, value]) => ({
    name: STATUS_LABELS[key] || key,
    value,
  }));

  const monthlyData = (data?.monthlyConversations || []).map((m) => ({
    month: m.month,
    conversations: m.count,
  }));

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 4 }}>
        <Box>
          <Typography fontWeight={800} fontSize={24} color="#1e293b">Platform Analytics</Typography>
          <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>Full platform overview with all metrics</Typography>
        </Box>
        <DateRangeToggle value={days} onChange={setDays} />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr" }, gap: "27px", mb: 5 }}>
        {stats.map((s) => (
            <StatCard {...s} />
        ))}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, gap: "27px", mb: 5 }}>
          <ChartCard title="Daily Conversation Trend" subtitle="Conversations per day" height={300}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyData}>
                <defs>
                  <linearGradient id="anColor" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.9)", backdropFilter: "blur(12px)", fontSize: 13 }} />
                <Area type="monotone" dataKey="conversations" stroke="#6366f1" strokeWidth={2} fill="url(#anColor)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Status Breakdown" subtitle="By conversation status" height={300}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={statusData} cx="50%" cy="50%" outerRadius={95} innerRadius={60} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {statusData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.9)", backdropFilter: "blur(12px)", fontSize: 13 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "27px", mb: 5 }}>
          <ChartCard title="Monthly Conversations" subtitle="Conversations per month (last 12)" height={280}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.9)", backdropFilter: "blur(12px)", fontSize: 13 }} />
                <Bar dataKey="conversations" fill="#6366f1" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <Paper className="glass-card" sx={{ p: 3.5, height: "100%" }}>
            <Typography fontWeight={700} fontSize={15} color="#1e293b" mb={3}>Recent Users</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {data?.recentUsers?.map((u) => (
                <Box key={u.id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={600} color="#1e293b">{u.name}</Typography>
                    <Typography variant="caption" color="#64748b">{u.email}</Typography>
                  </Box>
                  <Typography variant="caption" color="#64748b" sx={{ textTransform: "capitalize" }}>{u.role} &middot; {new Date(u.joined).toLocaleDateString()}</Typography>
                </Box>
              ))}
              {(!data?.recentUsers || data.recentUsers.length === 0) && (
                <Typography variant="body2" color="#64748b">No users yet</Typography>
              )}
            </Box>
          </Paper>
      </Box>
    </Box>
  );
};

export default Analytics;
